import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdClose } from "react-icons/io";
import { IoHelpCircleOutline, IoMusicalNotesOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { formatTextWithNewlines } from "../../helper/commonFunctions";
import AssignToDeckModal from "./AssignToDeckModal";
import { BsFolder2Open } from "react-icons/bs";
import Button from "../ui/Button";

const isInputHintType = (contentType) =>
    contentType === "inputhint" || contentType === "text/inputhint";

const renderContentPart = (part, index) => {
    const contentType = part?.contentType || "";
    const body = part?.contentBody;

    if (!body) return null;

    if (isInputHintType(contentType)) {
        return (
            <div key={index} className="rounded-lg border border-blue-200 bg-blue-50 p-3 my-2">
                <div className="flex items-center gap-1.5 mb-1">
                    <IoHelpCircleOutline className="text-blue-500" size={14} />
                    <span className="text-xs font-medium text-blue-600">Input hint</span>
                </div>
                <div
                    className="prose max-w-none text-sm text-blue-900"
                    dangerouslySetInnerHTML={{ __html: formatTextWithNewlines(body) }}
                />
            </div>
        );
    }

    if (contentType.includes("text") || contentType.startsWith("text")) {
        return (
            <div
                key={index}
                className="prose max-w-none text-sm my-1"
                dangerouslySetInnerHTML={{ __html: formatTextWithNewlines(body) }}
            />
        );
    }

    if (contentType.includes("image") || contentType.includes("unknown")) {
        return (
            <img
                key={index}
                src={body}
                alt="Content"
                className="max-w-full max-h-64 w-auto object-contain rounded-lg border my-2"
            />
        );
    }

    if (contentType.includes("audio")) {
        return (
            <audio key={index} controls className="w-full my-2">
                <source src={body} />
                Your browser does not support the audio element.
            </audio>
        );
    }

    if (contentType.includes("video")) {
        return (
            <video key={index} controls className="w-full max-w-sm rounded-lg my-2">
                <source src={body} />
                Your browser does not support the video element.
            </video>
        );
    }

    if (contentType === "test/correct" || contentType === "test/wrong") {
        const isCorrect = contentType === "test/correct";
        return (
            <div
                key={index}
                className={`flex items-start gap-2 p-2 rounded-lg border my-1 ${
                    isCorrect
                        ? "border-green-300 bg-green-50 text-green-700"
                        : "border-gray-200 bg-gray-50 text-gray-700"
                }`}
            >
                <span className="font-bold shrink-0">{isCorrect ? "✔" : "✖"}</span>
                <div
                    className="prose max-w-none text-sm"
                    dangerouslySetInnerHTML={{ __html: formatTextWithNewlines(body) }}
                />
            </div>
        );
    }

    // Fallback: show unknown content types as text so creators still see something
    return (
        <div key={index} className="text-sm text-gray-600 my-1 break-all">
            <span className="text-xs text-gray-400 uppercase tracking-wide">{contentType || "content"}: </span>
            {typeof body === "string" && (body.startsWith("http") || body.startsWith("blob:")) ? (
                <a href={body} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                    {body}
                </a>
            ) : (
                <span dangerouslySetInnerHTML={{ __html: formatTextWithNewlines(String(body)) }} />
            )}
        </div>
    );
};

const EmptyState = ({ label }) => (
    <p className="text-sm text-gray-400 italic py-2">{label}</p>
);

const AiAudioBlock = ({ title, textAI, audioUrl, t }) => {
    if (!textAI && !audioUrl) return null;

    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
            <h4 className="text-sm font-medium text-gray-700">{title}</h4>
            {textAI ? (
                <div>
                    <p className="text-xs text-gray-500 mb-0.5">
                        {t("ttsTextOptional") || "TTS text (optional)"}
                    </p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{textAI}</p>
                </div>
            ) : null}
            {audioUrl ? (
                <div>
                    <p className="text-xs text-gray-500 mb-0.5 flex items-center gap-1">
                        <IoMusicalNotesOutline size={12} />
                        {t("audioUrlOptional") || "Audio URL (optional)"}
                    </p>
                    <audio controls className="w-full h-8" src={audioUrl} />
                    <a
                        href={audioUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-blue-600 hover:underline break-all"
                    >
                        {audioUrl}
                    </a>
                </div>
            ) : null}
        </div>
    );
};

export default function SubjectOverview({ setIsDetail, isDetail, data, currentDeckId, complexity }) {
    const { t } = useTranslation();
    const [showAssignModal, setShowAssignModal] = useState(false);

    const handleClose = () => {
        setIsDetail(false);
    };

    const questionParts = data?.question?.parts || [];
    const answers = data?.answers || [];
    const hints = data?.hints || [];
    const markText = data?.mark?.text?.trim?.() || "";

    const hasAiAudio = Boolean(
        data?.questionTextAI ||
        data?.answerTextAI ||
        data?.hintTextAI ||
        data?.questionAudio ||
        data?.answerAudio ||
        data?.hintAudio ||
        data?.strictCheck
    );

    const answerPartsFlat = answers.flatMap((answer, ansIdx) =>
        (answer?.parts || []).map((part, partIdx) => ({
            ...part,
            _key: `${ansIdx}-${partIdx}`,
        }))
    );

    const hintPartsFlat = hints.flatMap((hint, hintIdx) =>
        (hint?.parts || []).map((part, partIdx) => ({
            ...part,
            _key: `h-${hintIdx}-${partIdx}`,
        }))
    );

    return (
        <Dialog
            open={isDetail}
            as="div"
            className="relative z-30 focus:outline-none"
            onClose={handleClose}
        >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                    <DialogPanel
                        transition
                        className="w-full relative max-w-4xl rounded-xl bg-white duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b">
                            <div className="min-w-0">
                                <h2 className="text-xl font-semibold text-gray-800 truncate">
                                    {data?.cardName || "Card Details"}
                                </h2>
                                {data?.cardId ? (
                                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                                        ID: {data.cardId}
                                    </p>
                                ) : null}
                            </div>
                            <IoMdClose
                                className="cursor-pointer text-gray-500 hover:text-gray-800 shrink-0"
                                fontSize={24}
                                onClick={handleClose}
                            />
                        </div>

                        {/* Body */}
                        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-6">
                            {/* Meta row */}
                            <div className="flex flex-wrap gap-3 text-sm">
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
                                    <strong className="mr-1">{t("answerType")}:</strong>
                                    {data?.answerType || "N/A"}
                                </span>
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
                                    <strong className="mr-1">{t("complexity")}:</strong>
                                    {complexity !== undefined && complexity !== null ? complexity : "N/A"}
                                </span>
                                {data?.status ? (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-50 text-amber-800">
                                        <strong className="mr-1">{t("status") || "Status"}:</strong>
                                        {data.status}
                                    </span>
                                ) : null}
                                {markText ? (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-orange-50 text-orange-800">
                                        <strong className="mr-1">{t("mark")}:</strong>
                                        {markText}
                                    </span>
                                ) : null}
                                {data?.language ? (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
                                        <strong className="mr-1">{t("language") || "Language"}:</strong>
                                        {data.language}
                                    </span>
                                ) : null}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Question */}
                                <div className="space-y-2">
                                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                                        {t("questions")}
                                    </h3>
                                    {questionParts.length > 0 ? (
                                        questionParts.map((part, i) => renderContentPart(part, i))
                                    ) : (
                                        <EmptyState label={t("noContent") || "No question content"} />
                                    )}
                                </div>

                                {/* Answer + Hints */}
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                                            {t("answer")}
                                            {answers.length > 1 ? (
                                                <span className="ml-2 text-sm font-normal text-gray-400">
                                                    ({answers.length} {t("alternatives") || "alternatives"})
                                                </span>
                                            ) : null}
                                        </h3>
                                        {answerPartsFlat.length > 0 ? (
                                            data?.answerType === "INPUT" && answers.length > 1 ? (
                                                answers.map((answer, ansIdx) => (
                                                    <div
                                                        key={ansIdx}
                                                        className="rounded-lg border border-gray-100 p-3 mb-2"
                                                    >
                                                        <p className="text-xs text-gray-400 mb-1">
                                                            {t("answerOption") || "Answer option"} {ansIdx + 1}
                                                            {answer?.score != null ? ` · score ${answer.score}` : ""}
                                                        </p>
                                                        {(answer?.parts || []).map((part, i) =>
                                                            renderContentPart(part, `${ansIdx}-${i}`)
                                                        )}
                                                    </div>
                                                ))
                                            ) : (
                                                answerPartsFlat.map((part, i) =>
                                                    renderContentPart(part, part._key || i)
                                                )
                                            )
                                        ) : (
                                            <EmptyState label={t("noContent") || "No answer content"} />
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                                            {t("Prompter")}
                                        </h3>
                                        {hintPartsFlat.length > 0 ? (
                                            hintPartsFlat.map((part, i) =>
                                                renderContentPart(part, part._key || i)
                                            )
                                        ) : (
                                            <EmptyState label={t("noHint") || "No hint"} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* AI / Audio (optional) — only when present */}
                            {hasAiAudio ? (
                                <div className="space-y-3 pt-2 border-t">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-700">
                                            {t("aiAudioSection") || "AI / Audio (optional)"}
                                        </h3>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            {t("aiAudioHelper") ||
                                                "Playback priority: audio URL → TTS from AI text → text from card parts."}
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <AiAudioBlock
                                            title={t("question") || "Question"}
                                            textAI={data?.questionTextAI}
                                            audioUrl={data?.questionAudio}
                                            t={t}
                                        />
                                        <AiAudioBlock
                                            title={t("answer") || "Answer"}
                                            textAI={data?.answerTextAI}
                                            audioUrl={data?.answerAudio}
                                            t={t}
                                        />
                                        <AiAudioBlock
                                            title={t("Prompter") || "Hint"}
                                            textAI={data?.hintTextAI}
                                            audioUrl={data?.hintAudio}
                                            t={t}
                                        />
                                    </div>

                                    {data?.strictCheck != null ? (
                                        <p className="text-sm text-gray-600">
                                            <strong>{t("strictCheck") || "Strict check"}:</strong>{" "}
                                            {data.strictCheck
                                                ? (t("yes") || "Yes")
                                                : (t("no") || "No")}
                                        </p>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>

                        {/* Footer */}
                        <div className="flex justify-between items-center p-4 bg-gray-50 rounded-b-xl border-t">
                            <div className="text-sm text-gray-600">
                                <span>
                                    <strong>{t("answerType")}:</strong> {data?.answerType || "N/A"}
                                </span>
                                <span className="ml-4">
                                    <strong>{t("complexity")}:</strong>{" "}
                                    {complexity !== undefined && complexity !== null ? complexity : "N/A"}
                                </span>
                            </div>
                            <Button
                                variant="primary"
                                onClick={() => setShowAssignModal(true)}
                            >
                                <BsFolder2Open size={16} />
                                {t("assignToDeck") || "Assign to Deck"}
                            </Button>
                        </div>
                    </DialogPanel>
                </div>
            </div>

            <AssignToDeckModal
                isOpen={showAssignModal}
                setIsOpen={setShowAssignModal}
                cardData={data}
                currentDeckId={currentDeckId}
                complexity={complexity}
            />
        </Dialog>
    );
}
